import type {
  FactorAssessment,
  FactorConfidence,
  FactorExplanation,
  FactorInfluence,
  FactorRole,
  SafetyExplanationSeverity,
  SafetyFactorExplanation,
  ScoreExplanation,
} from "@/lib/conditions/factor-explanations";
import { orderFactorExplanations } from "@/lib/conditions/factor-explanations";

type FactorFirstProps = {
  explanation: ScoreExplanation<SafetyFactorExplanation<SafetyExplanationSeverity>>;
  activeFit?: ActiveFitPresentation | null;
  title?: string;
};

export type ActiveFitPresentation = {
  readonly label: string;
  readonly assessment: FactorAssessment;
};

const ROLE_LABEL: Readonly<Record<FactorRole, string>> = {
  quality: "Quality factor",
  modifier: "Score modifier",
  structural: "Structural factor",
  suitability: "Suitability factor",
  safety: "Safety factor",
};

const INFLUENCE_STYLE: Readonly<Record<FactorInfluence, string>> = {
  "main limiter": "border-terracotta/40 bg-terracotta/10 text-text-body",
  "secondary limiter": "border-terracotta/30 bg-terracotta/5 text-text-body",
  "strong positive": "border-teal/30 bg-gold-pale text-teal-dark",
  "minor effect": "border-border bg-warm-bg text-text-body",
  neutral: "border-border bg-warm-bg text-text-muted",
  "not scored": "border-border bg-white text-text-muted",
};

function sentenceCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatReading(factor: FactorExplanation): string {
  if (factor.reading === null) return "Reading unavailable";
  return `${factor.reading}${factor.unit ? ` ${factor.unit}` : ""}`;
}

export function FactorHeadlineSummary({
  explanation,
  activeFit,
}: {
  explanation: ScoreExplanation<SafetyFactorExplanation<SafetyExplanationSeverity>>;
  activeFit?: ActiveFitPresentation | null;
}) {
  const mainLimiter = explanation.factors.find((factor) => factor.influence === "main limiter");
  const strongestPositive = explanation.factors.find((factor) => factor.influence === "strong positive");
  const items = [
    ...(mainLimiter ? [{ label: "Main limiter", value: mainLimiter.label }] : []),
    ...(strongestPositive ? [{ label: "Strongest positive", value: strongestPositive.label }] : []),
    ...(activeFit ? [{ label: "Active fit", value: `${activeFit.label}: ${sentenceCase(activeFit.assessment)}` }] : []),
    ...(explanation.confidence !== "full" ? [{ label: "Confidence", value: sentenceCase(explanation.confidence) }] : []),
  ];
  if (!mainLimiter && !strongestPositive && !activeFit) {
    return <p className="rounded-[12px] border border-border bg-warm-bg px-4 py-3 text-[12px] text-text-muted">
      {explanation.factors.length ? "No dominant limiter or strong positive was identified. The recorded factors are listed below." : "The stored score has no detailed factor breakdown. This will be shown after a refresh supplies the evidence."}{explanation.confidence !== "full" ? ` Forecast confidence: ${explanation.confidence}.` : ""}
    </p>;
  }
  return (
    <dl aria-label="What shapes this score" className="grid grid-cols-1 overflow-hidden rounded-[12px] border border-border bg-warm-bg sm:grid-cols-[repeat(auto-fit,minmax(140px,1fr))]">
      {items.map((item) => <SummaryItem key={item.label} {...item} />)}
    </dl>
  );
}

function SummaryItem({ label, value, className = "" }: { label: string; value: string; className?: string }) {
  return (
    <div className={`min-w-0 border-b border-r border-border px-3 py-3 last:border-r-0 md:border-b-0 ${className}`}>
      <dt className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-muted">{label}</dt>
      <dd className="mt-1 break-words text-[12.5px] font-semibold leading-snug text-navy">{value}</dd>
    </div>
  );
}

function confidenceCopy(confidence: FactorConfidence): string {
  const copy: Readonly<Record<FactorConfidence, string>> = {
    full: "Input available",
    partial: "Partial input",
    inferred: "Inferred input",
    unavailable: "Input unavailable",
  };
  return copy[confidence];
}

function FactorRow({ factor }: { factor: FactorExplanation }) {
  return (
    <li className="border-t border-border first:border-t-0">
      <details className="group">
        <summary className="flex min-h-11 min-w-11 cursor-pointer list-none items-center gap-3 rounded-[8px] px-1 py-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden">
          <span
            aria-hidden="true"
            className={`h-8 w-1 shrink-0 rounded-full ${
              factor.influence === "strong positive"
                ? "bg-teal"
                : factor.influence.includes("limiter")
                  ? "bg-terracotta"
                  : "bg-sage"
            }`}
          />
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-[13px] font-bold text-navy">{factor.label}</span>
              <span className="text-[13px] font-semibold tabular-nums text-text-body">{formatReading(factor)}</span>
              <span className="text-[12px] text-text-muted">{sentenceCase(factor.assessment)}</span>
            </span>
            <span className="mt-1 flex flex-wrap gap-1.5">
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.04em] ${INFLUENCE_STYLE[factor.influence]}`}>
                {sentenceCase(factor.influence)}
              </span>
              <span className="rounded-full border border-border bg-white px-2 py-0.5 text-[10px] font-semibold text-text-muted">
                {ROLE_LABEL[factor.role]}
              </span>
            </span>
          </span>
          <span aria-hidden="true" className="shrink-0 text-[18px] leading-none text-text-muted transition-transform motion-reduce:transition-none group-open:rotate-45">+</span>
        </summary>
        <div className="ml-5 border-l border-border pb-3 pl-4 pr-2">
          <p className="text-[13px] leading-[1.55] text-text-body">{factor.why}</p>
          <p className="mt-1.5 text-[11px] font-semibold text-text-muted">{confidenceCopy(factor.confidence)}</p>
        </div>
      </details>
    </li>
  );
}

function FactorGroup({
  title,
  description,
  factors,
  ordered = false,
}: {
  title: string;
  description: string;
  factors: readonly FactorExplanation[];
  ordered?: boolean;
}) {
  if (factors.length === 0) return null;
  const List = ordered ? "ol" : "ul";
  return (
    <section aria-label={title}>
      <div className="mb-2">
        <h3 className="font-serif text-[19px] font-bold text-navy">{title}</h3>
        <p className="mt-0.5 text-[12px] leading-[1.45] text-text-muted">{description}</p>
      </div>
      <List aria-label={title} className="border-y border-border">
        {factors.map((factor) => <FactorRow key={factor.key} factor={factor} />)}
      </List>
    </section>
  );
}

export function FactorFirst({ explanation, activeFit, title = "Why this score" }: FactorFirstProps) {
  const orderedFactors = orderFactorExplanations(explanation.factors);
  const quality = orderedFactors.filter((factor) =>
    factor.role === "quality" || factor.role === "modifier" ||
    factor.role === "structural"
  );
  const suitability = orderedFactors.filter((factor) => factor.role === "suitability");
  const safetyByKey = new Map<string, FactorExplanation>();
  explanation.factors.filter((factor) => factor.role === "safety").forEach((factor) => safetyByKey.set(factor.key, factor));
  explanation.safety.forEach((factor) => safetyByKey.set(factor.key, factor));
  const safety = [...safetyByKey.values()];

  return (
    <section aria-label={title} className="rounded-[16px] border border-border-light bg-white p-4 md:p-5">
      <header className="mb-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-teal-dark">Conditions ledger</p>
        <h2 className="mt-0.5 font-serif text-[24px] font-bold leading-tight text-navy">{title}</h2>
        <p className="mt-1 text-[13px] leading-[1.5] text-text-muted">
          Read the biggest influences first. Open any factor to see why it matters for this sport.
        </p>
      </header>
      <FactorHeadlineSummary explanation={explanation} activeFit={activeFit} />
      <div className="mt-5 space-y-6">
        <FactorGroup title="Score factors" description="These conditions shape the quality score, in the order shown." factors={quality} ordered />
        <FactorGroup title="Who it suits" description="Suitability helps you judge the fit without changing the headline score." factors={suitability} />
        <FactorGroup title="Safety checks" description="Safety is considered separately and never earns quality points." factors={safety} />
      </div>
    </section>
  );
}
